import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Pencil, X, Images } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, getAdminToken, formatApiErrorDetail } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";

const MAX_PHOTOS = 5;

export const ProductEditDialog = ({ product, open, onOpenChange, onSaved }) => {
    const [name, setName] = useState("");
    const [category, setCategory] = useState("");
    const [price, setPrice] = useState("");
    const [description, setDescription] = useState("");
    const [images, setImages] = useState([]);
    const [newFiles, setNewFiles] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (product && open) {
            setName(product.name || "");
            setCategory(product.category || "");
            setPrice(((product.price_cents || 0) / 100).toFixed(2).replace(".", ","));
            setDescription(product.description || "");
            setImages(product.images?.length ? [...product.images] : [product.storage_path].filter(Boolean));
            setNewFiles([]);
        }
    }, [product, open]);

    const removeImage = (p) => setImages((prev) => prev.filter((x) => x !== p));

    const pickFiles = (e) => {
        const picked = Array.from(e.target.files || []);
        e.target.value = "";
        const next = [...newFiles];
        for (const f of picked) {
            if (!(f.type || "").startsWith("image/")) {
                toast.error(`"${f.name}" não é uma imagem.`);
                continue;
            }
            if (f.size > 10 * 1024 * 1024) {
                toast.error(`"${f.name}" passa de 10MB.`);
                continue;
            }
            if (images.length + next.length >= MAX_PHOTOS) {
                toast.error(`Máximo de ${MAX_PHOTOS} fotos no total.`);
                break;
            }
            next.push(f);
        }
        setNewFiles(next);
    };

    const save = async (e) => {
        e.preventDefault();
        if (!name.trim()) {
            toast.error("Dê um nome para a peça.");
            return;
        }
        setSaving(true);
        try {
            const uploaded = [];
            for (const f of newFiles) {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/uploads/image", fd, {
                    headers: { Authorization: `Bearer ${getAdminToken()}` },
                });
                uploaded.push(data.path);
            }
            const finalImages = [...images, ...uploaded];
            if (!finalImages.length) {
                toast.error("A peça precisa de pelo menos 1 foto.");
                return;
            }
            await api.patch(
                `/products/${product.id}`,
                { name, category, price, description, images: finalImages },
                { headers: { Authorization: `Bearer ${getAdminToken()}` } },
            );
            toast.success("Peça atualizada no catálogo!");
            onSaved?.();
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-lenis-prevent="true"
                data-testid="product-edit-dialog"
                className="max-h-[90vh] max-w-lg overflow-y-auto border-forge-line bg-forge-panel text-forge-paper"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display text-lg uppercase text-forge-yellow">
                        <Pencil className="h-4 w-4" />
                        Editar peça
                    </DialogTitle>
                    <DialogDescription className="text-xs text-forge-dim">
                        Altere o que quiser e salve — a peça continua no ar, sem precisar excluir e criar de novo.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={save} className="space-y-4" data-testid="product-edit-form">
                    <div className="space-y-2">
                        <Label htmlFor="edit-name" className="font-mono text-[11px] uppercase tracking-[0.2em] text-forge-dim">
                            Nome
                        </Label>
                        <Input
                            id="edit-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            data-testid="edit-name-input"
                            className="border-forge-line bg-forge-ink"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label htmlFor="edit-category" className="font-mono text-[11px] uppercase tracking-[0.2em] text-forge-dim">
                                Categoria
                            </Label>
                            <Input
                                id="edit-category"
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                data-testid="edit-category-input"
                                className="border-forge-line bg-forge-ink"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="edit-price" className="font-mono text-[11px] uppercase tracking-[0.2em] text-forge-dim">
                                Preço (R$)
                            </Label>
                            <Input
                                id="edit-price"
                                value={price}
                                onChange={(e) => setPrice(e.target.value)}
                                inputMode="decimal"
                                data-testid="edit-price-input"
                                className="border-forge-line bg-forge-ink"
                            />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label className="font-mono text-[11px] uppercase tracking-[0.2em] text-forge-dim">
                            Descrição formatada
                        </Label>
                        <RichTextEditor
                            value={description}
                            resetKey={product?.id || "nada"}
                            onChange={setDescription}
                            testId="edit-description-editor"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-forge-dim">
                            <Images className="h-3.5 w-3.5" />
                            Fotos ({images.length + newFiles.length}/{MAX_PHOTOS})
                        </Label>
                        <div className="flex flex-wrap gap-2" data-testid="edit-photo-list">
                            {images.map((p) => (
                                <div key={p} data-testid="edit-photo-item" className="relative">
                                    <img src={fileUrl(p)} alt="" className="h-16 w-16 border border-forge-line object-cover" />
                                    <button
                                        type="button"
                                        onClick={() => removeImage(p)}
                                        aria-label="Remover foto"
                                        data-testid={`edit-photo-remove-${p.slice(-8)}`}
                                        className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white transition-transform duration-300 hover:scale-110"
                                    >
                                        <X className="h-3 w-3" />
                                    </button>
                                </div>
                            ))}
                            {newFiles.map((f, i) => (
                                <div key={`${f.name}-${i}`} data-testid="edit-photo-item-new" className="relative">
                                    <img src={URL.createObjectURL(f)} alt="" className="h-16 w-16 border border-dashed border-forge-yellow/60 object-cover" />
                                    <button
                                        type="button"
                                        onClick={() => setNewFiles(newFiles.filter((_, idx) => idx !== i))}
                                        aria-label="Remover foto nova"
                                        className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white transition-transform duration-300 hover:scale-110"
                                    >
                                        <X className="h-3 w-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <Input
                            type="file"
                            accept="image/*"
                            multiple
                            data-testid="edit-file-input"
                            onChange={pickFiles}
                            disabled={images.length + newFiles.length >= MAX_PHOTOS}
                            className="border-forge-line bg-forge-ink text-xs text-forge-paper file:mr-3 file:border-0 file:bg-forge-yellow file:px-2 file:py-1 file:font-mono file:text-[10px] file:uppercase file:tracking-widest file:text-forge-ink"
                        />
                    </div>
                    <Button
                        type="submit"
                        disabled={saving}
                        data-testid="edit-save-button"
                        className="w-full bg-forge-yellow font-mono text-xs uppercase tracking-[0.2em] text-forge-ink hover:bg-forge-yellow/90"
                    >
                        {saving ? "Salvando..." : "Salvar alterações"}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default ProductEditDialog;
